import React, { useCallback, useEffect, useMemo, useState } from 'react';

const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '') || '';

function formatTs(ts) {
  if (!ts) return '-';
  try { return new Date(ts * 1000).toLocaleString(); } catch { return String(ts); }
}

export default function AdminPage() {
  const baseUrl = useMemo(() => apiBase || `${window.location.origin}`, []);
  const [token, setToken] = useState(() => sessionStorage.getItem('adminToken') || '');
  const [verified, setVerified] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [envDebug, setEnvDebug] = useState(false);
  // users state
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [usersResp, setUsersResp] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [creditsInput, setCreditsInput] = useState({}); // { [id]: string }
  const [selected, setSelected] = useState({}); // { [id]: true }
  const [bulkAmount, setBulkAmount] = useState('');
  const [onlyZero, setOnlyZero] = useState(false);
  const [pwFilter, setPwFilter] = useState('any'); // any | yes | no
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [notifyUsers, setNotifyUsers] = useState(true);

  // Derived list view must be computed before any early returns so hooks order is stable
  const viewUsers = useMemo(() => {
    let arr = usersResp?.users ? [...usersResp.users] : [];
    if (onlyZero) arr = arr.filter(u => (u.free_credits ?? 0) === 0);
    if (pwFilter === 'yes') arr = arr.filter(u => !!u.has_password);
    if (pwFilter === 'no') arr = arr.filter(u => !u.has_password);
    const cmp = (a,b) => {
      const va = a?.[sortBy];
      const vb = b?.[sortBy];
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortBy === 'email') return (String(va||'').localeCompare(String(vb||''))) * dir;
      const na = Number(va ?? 0);
      const nb = Number(vb ?? 0);
      if (na === nb) return 0;
      return na < nb ? -1*dir : 1*dir;
    };
    try { arr.sort(cmp); } catch {}
    return arr;
  }, [usersResp, onlyZero, pwFilter, sortBy, sortDir]);

  // Auto-dismiss brief success message
  useEffect(() => {
    if (!okMsg) return;
    const t = setTimeout(() => setOkMsg(''), 1800);
    return () => clearTimeout(t);
  }, [okMsg]);

  useEffect(() => {
    // attempt silent verify if token exists
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${baseUrl}/admin/cleanup`, { headers: { 'X-Admin-Token': token }, credentials: 'include' });
        if (!r.ok) throw new Error('Token invalid');
        setVerified(true);
        setVerifyMsg('Token verified');
      } catch {
        setVerified(false);
      }
    })();
  }, [baseUrl, token]);

  const saveToken = useCallback(() => {
    if (!token) return;
    sessionStorage.setItem('adminToken', token);
  }, [token]);

  const signOut = useCallback(() => {
    sessionStorage.removeItem('adminToken');
    try { sessionStorage.removeItem('adminVerified'); window.dispatchEvent(new CustomEvent('admin-verified', { detail: false })); } catch {}
    setToken('');
    setVerified(false);
    setUsersResp(null);
    setVerifyMsg('Signed out');
  }, []);

  async function verifyNow() {
    setError(''); setVerifyMsg(''); setLoading(true);
    try {
      const r = await fetch(`${baseUrl}/admin/cleanup`, { headers: { 'X-Admin-Token': token }, credentials: 'include' });
      if (!r.ok) throw new Error('Invalid admin token');
      setVerified(true);
      setVerifyMsg('Token verified');
      saveToken();
      try { sessionStorage.setItem('adminVerified', '1'); window.dispatchEvent(new CustomEvent('admin-verified', { detail: true })); } catch {}
      try {
        const h = await fetch(`${baseUrl}/health`, { credentials: 'include' });
        const hj = await h.json();
        if (typeof hj.debug === 'boolean') setEnvDebug(hj.debug);
      } catch {}
    } catch (e) {
      setVerified(false);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadUsers(nextOffset = offset) {
    setError(''); setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (limit) params.set('limit', String(limit));
      if (nextOffset) params.set('offset', String(nextOffset));
      const r = await fetch(`${baseUrl}/admin/users?${params.toString()}`, { headers: { 'X-Admin-Token': token }, credentials:'include' });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      setUsersResp(j);
      setOffset(nextOffset);
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }

  async function runCleanup() {
    setError(''); setLoading(true);
    try {
      const r = await fetch(`${baseUrl}/admin/cleanup`, { method:'POST', headers: { 'X-Admin-Token': token }, credentials:'include' });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      setVerifyMsg(`Cleanup ran, removed: ${j.trigger_removed ?? 0}`);
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }

  async function getCleanupStatus() {
    setError(''); setLoading(true);
    try {
      const r = await fetch(`${baseUrl}/admin/cleanup`, { headers: { 'X-Admin-Token': token }, credentials:'include' });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      setVerifyMsg(`Shared dir files: ${j.shared?.file_count ?? 0}, total_removed: ${j.total_removed ?? 0}`);
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }

  async function deleteUser(email) {
    setError(''); setLoading(true);
    try {
      const u = new URL(`${baseUrl}/admin/users`);
      u.searchParams.set('email', email);
      const r = await fetch(u.toString(), { method:'DELETE', headers: { 'X-Admin-Token': token }, credentials:'include' });
      if (!r.ok) throw new Error(await r.text());
      await loadUsers(offset);
      setConfirmEmail('');
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }

  if (!verified) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h2 style={{marginTop:0}}>Admin Access</h2>
          <p style={{color:'#64748b'}}>Enter your admin token to continue.</p>
          <input type="password" value={token} onChange={e=>setToken(e.target.value)} placeholder="X-Admin-Token" style={styles.input} />
          <div style={{display:'flex', gap:8, marginTop:8}}>
            <button onClick={verifyNow} disabled={!token || loading} style={styles.primaryBtn}>Verify</button>
            <a href="/" style={styles.linkBtn}>Back to app</a>
          </div>
          {verifyMsg && <div style={styles.note}>{verifyMsg}</div>}
          {error && <div style={styles.error} role="alert">{error}</div>}
        </div>
      </div>
    );
  }

  const canPrev = offset > 0;
  const canNext = usersResp && usersResp.total > offset + limit;

  function toggleSort(field) {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  }

  function exportCsv() {
    const rows = [
      ['id','email','free_credits','used_credits','created_at','last_login','has_password']
    ];
    (viewUsers||[]).forEach(u => {
      rows.push([
        u.id,
        u.email,
        u.free_credits,
        u.used_credits ?? 0,
        formatTs(u.created_at),
        formatTs(u.last_login),
        u.has_password ? 'yes' : 'no'
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `users_${Date.now()}.csv`; a.click();
    setTimeout(()=>URL.revokeObjectURL(url), 1000);
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={{margin:'0 0 4px'}}>Admin Console</h1>
          <div style={{color:'#64748b'}}>Env debug: {envDebug ? 'ON' : 'off'}</div>
        </div>
        <div style={{display:'flex', gap:8}}>
          <button onClick={signOut} style={styles.btn}>Sign out</button>
          <a href="/" style={styles.linkBtn}>Back to app</a>
        </div>
      </div>

      {error && <div style={styles.error} role="alert">{error}</div>}
      {verifyMsg && <div style={styles.note}>{verifyMsg}</div>}
  {okMsg && <div style={styles.note}>{okMsg}</div>}

      <div style={styles.grid}>
        <section style={styles.card}>
          <h3>Cleanup</h3>
          <p style={{color:'#64748b'}}>Manage shared images cleanup job. Use “Status” to view quick stats, or “Run now”.</p>
          <div style={{display:'flex', gap:8}}>
            <button onClick={getCleanupStatus} disabled={loading} style={styles.btn}>Status</button>
            <button onClick={runCleanup} disabled={loading} style={styles.btn}>Run now</button>
          </div>
        </section>

        <section style={styles.card}>
          <h3>Users</h3>
          <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
            <input placeholder="email contains..." value={q} onChange={e=>setQ(e.target.value)} style={styles.input} />
            <label>Limit</label>
            <input type="number" min={1} max={1000} value={limit} onChange={e=>setLimit(Number(e.target.value)||50)} style={{...styles.input, width:100}} />
            <button onClick={()=>loadUsers(0)} disabled={loading} style={styles.primaryBtn}>Search</button>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginTop:8}}>
            <label style={{display:'flex', alignItems:'center', gap:6}}>
              <input type="checkbox" checked={onlyZero} onChange={e=>setOnlyZero(e.target.checked)} />
              Zero credits only
            </label>
            <label style={{display:'flex', alignItems:'center', gap:6}}>
              Password:
              <select value={pwFilter} onChange={e=>setPwFilter(e.target.value)} style={{...styles.input, padding:'4px 6px'}}>
                <option value="any">Any</option>
                <option value="yes">Has password</option>
                <option value="no">No password</option>
              </select>
            </label>
            <label style={{display:'flex', alignItems:'center', gap:6}} title="Send a confirmation email to users when credits change">
              <input type="checkbox" checked={notifyUsers} onChange={e=>setNotifyUsers(e.target.checked)} />
              Email user about changes
            </label>
            <div style={{marginLeft:'auto', display:'flex', gap:8}}>
              <button onClick={exportCsv} disabled={!viewUsers?.length} style={styles.btn}>Export CSV</button>
            </div>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginTop:8}}>
            <span style={{color:'#64748b'}}>Selected: {Object.keys(selected).filter(k=>selected[k]).length}</span>
            <input placeholder="amount" value={bulkAmount} onChange={e=>setBulkAmount(e.target.value)} style={{...styles.input, width:110}} />
            <button
              onClick={async ()=>{
                const v = parseInt(bulkAmount || '');
                if (Number.isNaN(v)) { setError('Enter a number'); return; }
                const ids = Object.keys(selected).filter(k=>selected[k]).map(Number);
                if (!ids.length) { setError('Select at least one user'); return; }
                setLoading(true); setError('');
                try {
                  for (const id of ids) {
                    const u = viewUsers.find(x=>x.id===id) || (usersResp?.users||[]).find(x=>x.id===id);
                    if (!u) continue;
                    const r = await fetch(`${baseUrl}/admin/users/credits`, {
                      method:'POST', headers:{ 'Content-Type':'application/json', 'X-Admin-Token': token }, credentials:'include',
                      body: JSON.stringify({ email: u.email, delta: v, notify_user: !!notifyUsers })
                    });
                    if (!r.ok) throw new Error(await r.text());
                  }
                  await loadUsers(offset);
                  setOkMsg('Updated credits');
                } catch(e){ setError(e.message||String(e)); }
                finally { setLoading(false); }
              }}
              disabled={loading}
              style={styles.btn}
            >Add to selected</button>
            <button
              onClick={async ()=>{
                const v = parseInt(bulkAmount || '');
                if (Number.isNaN(v)) { setError('Enter a number'); return; }
                const ids = Object.keys(selected).filter(k=>selected[k]).map(Number);
                if (!ids.length) { setError('Select at least one user'); return; }
                setLoading(true); setError('');
                try {
                  for (const id of ids) {
                    const u = viewUsers.find(x=>x.id===id) || (usersResp?.users||[]).find(x=>x.id===id);
                    if (!u) continue;
                    const r = await fetch(`${baseUrl}/admin/users/credits`, {
                      method:'POST', headers:{ 'Content-Type':'application/json', 'X-Admin-Token': token }, credentials:'include',
                      body: JSON.stringify({ email: u.email, set_to: v, notify_user: !!notifyUsers })
                    });
                    if (!r.ok) throw new Error(await r.text());
                  }
                  await loadUsers(offset);
                  setOkMsg('Updated credits');
                } catch(e){ setError(e.message||String(e)); }
                finally { setLoading(false); }
              }}
              disabled={loading}
              style={styles.primaryBtn}
            >Set for selected</button>
            <button onClick={()=>setSelected({})} disabled={!Object.keys(selected).some(k=>selected[k])} style={styles.btn}>Clear selection</button>
          </div>
          <div style={{marginTop:10, overflow:'auto'}}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.center}
                  >
                    <input
                      type="checkbox"
                      checked={viewUsers?.length ? viewUsers.every(u=>selected[u.id]) : false}
                      onChange={e=>{
                        const checked = e.target.checked;
                        setSelected(prev => {
                          const next = {...prev};
                          (viewUsers||[]).forEach(u => { next[u.id] = checked; });
                          return next;
                        });
                      }}
                    />
                  </th>
                  <th style={styles.center}>ID</th>
                  <th onClick={()=>toggleSort('email')} style={styles.thSort}>Email {sortBy==='email' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={()=>toggleSort('free_credits')} style={{...styles.thSort, ...styles.center}}>Free credits {sortBy==='free_credits' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={()=>toggleSort('used_credits')} style={{...styles.thSort, ...styles.center}}>Used credits {sortBy==='used_credits' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th style={styles.center}>Adjust</th>
                  <th onClick={()=>toggleSort('created_at')} style={{...styles.thSort, ...styles.center}}>Created {sortBy==='created_at' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={()=>toggleSort('last_login')} style={{...styles.thSort, ...styles.center}}>Last login {sortBy==='last_login' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th style={styles.center}>Password?</th>
                  <th style={styles.center}>Delete</th>
                </tr>
              </thead>
              <tbody>
                {viewUsers?.length ? viewUsers.map(u => (
                  <tr key={u.id}>
                    <td style={styles.center}>
                      <input type="checkbox" checked={!!selected[u.id]} onChange={e=>setSelected(s=>({ ...s, [u.id]: e.target.checked }))} />
                    </td>
                    <td style={styles.center}>{u.id}</td>
                    <td>{u.email}</td>
                    <td style={styles.center}>{u.free_credits}</td>
                    <td style={styles.center}>{u.used_credits ?? 0}</td>
                    <td style={styles.center}>
                      <div style={{display:'flex', gap:6, alignItems:'center'}}>
                        <button
                          onClick={async ()=>{
                            setLoading(true); setError('');
                            try {
                              const r = await fetch(`${baseUrl}/admin/users/credits`, {
                                method:'POST', headers:{ 'Content-Type':'application/json', 'X-Admin-Token': token }, credentials:'include',
                                body: JSON.stringify({ email: u.email, delta: -1, notify_user: !!notifyUsers })
                              });
                              if (!r.ok) throw new Error(await r.text());
                              await loadUsers(offset);
                              setOkMsg('Updated credits');
                            } catch(e){ setError(e.message||String(e)); }
                            finally { setLoading(false); }
                          }}
                          disabled={loading}
                          style={styles.smBtn}
                          title="Decrement by 1"
                        >-1</button>
                        <button
                          onClick={async ()=>{
                            setLoading(true); setError('');
                            try {
                              const r = await fetch(`${baseUrl}/admin/users/credits`, {
                                method:'POST', headers:{ 'Content-Type':'application/json', 'X-Admin-Token': token }, credentials:'include',
                                body: JSON.stringify({ email: u.email, delta: 1, notify_user: !!notifyUsers })
                              });
                              if (!r.ok) throw new Error(await r.text());
                              await loadUsers(offset);
                              setOkMsg('Updated credits');
                            } catch(e){ setError(e.message||String(e)); }
                            finally { setLoading(false); }
                          }}
                          disabled={loading}
                          style={styles.smBtn}
                          title="Increment by 1"
                        >+1</button>
                        <input
                          placeholder={"amount"}
                          value={creditsInput[u.id] ?? ''}
                          onChange={e=>setCreditsInput(ci=>({ ...ci, [u.id]: e.target.value }))}
                          style={{...styles.input, width:90}}
                          inputMode="numeric"
                        />
                        <button
                          onClick={async ()=>{
                            const v = parseInt(creditsInput[u.id] ?? '');
                            if (Number.isNaN(v)) { setError('Enter a number'); return; }
                            setLoading(true); setError('');
                            try {
                              const r = await fetch(`${baseUrl}/admin/users/credits`, {
                                method:'POST',
                                headers:{ 'Content-Type':'application/json', 'X-Admin-Token': token },
                                credentials:'include',
                                body: JSON.stringify({ email: u.email, delta: v, notify_user: !!notifyUsers })
                              });
                              if (!r.ok) throw new Error(await r.text());
                              await loadUsers(offset);
                              setOkMsg('Updated credits');
                            } catch(e){ setError(e.message||String(e)); }
                            finally { setLoading(false); }
                          }}
                          disabled={loading}
                          style={styles.btn}
                        >Add</button>
                        <button
                          onClick={async ()=>{
                            const v = parseInt(creditsInput[u.id] ?? '');
                            if (Number.isNaN(v)) { setError('Enter a number'); return; }
                            setLoading(true); setError('');
                            try {
                              const r = await fetch(`${baseUrl}/admin/users/credits`, {
                                method:'POST',
                                headers:{ 'Content-Type':'application/json', 'X-Admin-Token': token },
                                credentials:'include',
                                body: JSON.stringify({ email: u.email, set_to: v, notify_user: !!notifyUsers })
                              });
                              if (!r.ok) throw new Error(await r.text());
                              await loadUsers(offset);
                              setOkMsg('Updated credits');
                            } catch(e){ setError(e.message||String(e)); }
                            finally { setLoading(false); }
                          }}
                          disabled={loading}
                          style={styles.primaryBtn}
                        >Set</button>
                      </div>
                    </td>
                    <td style={styles.center}>{formatTs(u.created_at)}</td>
                    <td style={styles.center}>{formatTs(u.last_login)}</td>
                    <td style={styles.center}>{u.has_password ? 'Yes' : 'No'}</td>
                    <td style={styles.center}>
                      <div style={{display:'flex', gap:6, alignItems:'center'}}>
                        <input placeholder="type email to confirm" value={confirmEmail} onChange={e=>setConfirmEmail(e.target.value)} style={{...styles.input, width:200}} />
                        <button onClick={()=>deleteUser(u.email)} disabled={loading || confirmEmail !== u.email} style={styles.dangerBtn}>Delete</button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={10} style={{textAlign:'center', color:'#64748b'}}>{loading ? 'Loading...' : 'No results'}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{display:'flex', justifyContent:'center', alignItems:'center', gap:10, marginTop:10}}>
            <button onClick={()=>loadUsers(Math.max(0, offset - limit))} disabled={!canPrev || loading} style={styles.btn}>Prev</button>
            <span style={{color:'#64748b'}}>Offset {offset} / Total {usersResp?.total ?? 0}</span>
            <button onClick={()=>loadUsers(offset + limit)} disabled={!canNext || loading} style={styles.btn}>Next</button>
          </div>
        </section>
      </div>
    </div>
  );
}

const styles = {
  page: { padding:'16px', maxWidth:1200, margin:'0 auto' },
  header: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 },
  grid: { display:'grid', gridTemplateColumns:'1fr', gap:16 },
  card: { background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, padding:12, boxShadow:'0 1px 2px rgba(0,0,0,0.03)' },
  input: { padding:'6px 8px', border:'1px solid #cbd5e1', borderRadius:6 },
  primaryBtn: { padding:'8px 12px', background:'#2563eb', color:'#fff', border:'none', borderRadius:6, cursor:'pointer' },
  btn: { padding:'8px 12px', background:'#f1f5f9', color:'#111', border:'1px solid #e5e7eb', borderRadius:6, cursor:'pointer' },
  dangerBtn: { padding:'8px 12px', background:'#dc2626', color:'#fff', border:'none', borderRadius:6, cursor:'pointer' },
  smBtn: { padding:'4px 8px', background:'#f8fafc', color:'#111', border:'1px solid #e5e7eb', borderRadius:6, cursor:'pointer' },
  error: { color:'#b91c1c', background:'#fee2e2', border:'1px solid #fecaca', padding:'8px 10px', borderRadius:6, marginBottom:10 },
  note: { color:'#065f46', background:'#ecfdf5', border:'1px solid #a7f3d0', padding:'6px 8px', borderRadius:6, marginTop:10 },
  table: { width:'100%', borderCollapse:'collapse' },
  linkBtn: { padding:'8px 12px', border:'1px solid #cbd5e1', borderRadius:6, color:'#111', textDecoration:'none', background:'#fff' },
  thSort: { cursor:'pointer', whiteSpace:'nowrap' },
  center: { textAlign:'center' },
};
